import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { CheckIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import type {
  ListboxPlacement,
  ListboxProps,
  ListboxTriggerState,
  DefaultTriggerProps,
} from './Listbox.types';

/** Placement → where the panel sits, and where it slides in from. */
const PANEL_POSITION: Record<ListboxPlacement, { at: string; from: string }> = {
  bottom: { at: 'left-0 right-0 top-full mt-2', from: '-translate-y-1' },
  top: { at: 'left-0 right-0 bottom-full mb-2', from: 'translate-y-2' },
};

/** The default trigger: the current label, and a chevron pointing at the panel. */
function DefaultTrigger({
  label,
  placement,
  state,
}: DefaultTriggerProps): React.JSX.Element {
  const { isOpen, currentLabel, triggerProps } = state;
  // Points where the panel will appear, and flips to point back at the trigger
  // once it is there.
  const pointsUp = isOpen ? placement === 'bottom' : placement === 'top';

  return (
    <button
      {...triggerProps}
      aria-label={label}
      className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-stroke-default bg-contrast-700 px-3 text-body-md text-neutral-50 transition-colors duration-fast hover:bg-contrast-600 focus:outline-none focus-visible:ring-state-focus-inset"
    >
      <span className="truncate">{currentLabel}</span>
      <ChevronDownIcon
        className={`h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-normal ease-out motion-reduce:transition-none ${
          pointsUp ? 'rotate-180' : ''
        }`}
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * Listbox - a styled replacement for `<select>`: a trigger showing the current
 * option, and a panel of options with a checkmark on the selected one.
 *
 * There were two of these, 301 and 183 lines, both implementing `role="listbox"`
 * with `aria-activedescendant`, roving focus, scroll-into-view, click-outside
 * and Escape. The Reports one's docstring even said it was "matching the
 * dashboard's view-mode switcher" — matching it by hand, in a second file.
 *
 * Focus never leaves the trigger. The list is navigated with
 * `aria-activedescendant`, so the options are not tab stops and the keyboard
 * handler lives on the button.
 *
 * `components/forms/FormSelectize` is a third combobox-shaped thing and stays
 * out: it does async search, multi-select and free text, which is a different
 * widget wearing a similar hat.
 *
 * @example
 * <Listbox value={period} options={PERIODS} onChange={setPeriod}
 *          label="Report period" />
 *
 * // A custom trigger — the listbox still owns everything but the button
 * <Listbox
 *   placement="top"
 *   renderTrigger={({ isOpen, currentLabel, triggerProps }) => (
 *     <button {...triggerProps} className="...">{currentLabel}</button>
 *   )}
 *   … />
 */
export function Listbox({
  value,
  options,
  onChange,
  label,
  placement = 'bottom',
  renderTrigger,
  panelClassName = '',
  className = '',
  'data-testid': testId,
}: ListboxProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const baseId = useId();

  const currentLabel =
    options.find((option) => option.value === value)?.label ?? '';

  // Opening lands on the current value, not the top of the list.
  const open = useCallback(() => {
    const currentIndex = options.findIndex((option) => option.value === value);
    setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
    setIsOpen(true);
  }, [options, value]);

  const close = useCallback((refocus = true) => {
    setIsOpen(false);
    setFocusedIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const select = useCallback(
    (next: string) => {
      onChange(next);
      close();
    },
    [onChange, close]
  );

  // Dismiss on click outside.
  useEffect(() => {
    if (!isOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isOpen, close]);

  // Keep the focused option scrolled into view.
  useEffect(() => {
    if (!isOpen || focusedIndex < 0) return;
    const items = listRef.current?.querySelectorAll('[role="option"]');
    const item = items?.[focusedIndex] as HTMLElement | undefined;
    item?.scrollIntoView?.({ block: 'nearest' });
  }, [isOpen, focusedIndex]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          open();
        }
        return;
      }
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          close();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % options.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) =>
            prev <= 0 ? options.length - 1 : prev - 1
          );
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(options.length - 1);
          break;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const option = options[focusedIndex];
          if (option) select(option.value);
          break;
        }
      }
    },
    [isOpen, open, close, select, options, focusedIndex]
  );

  const triggerState: ListboxTriggerState = {
    isOpen,
    currentLabel,
    triggerProps: {
      ref: triggerRef,
      type: 'button',
      onClick: () => (isOpen ? close(false) : open()),
      onKeyDown,
      'aria-haspopup': 'listbox',
      'aria-expanded': isOpen,
    },
  };

  const position = PANEL_POSITION[placement];

  const panel = (
    <div
      aria-hidden={!isOpen}
      className={`absolute z-10 ${position.at} transform transition-all duration-normal ease-out motion-reduce:transition-none ${
        isOpen
          ? 'translate-y-0 opacity-100'
          : `pointer-events-none opacity-0 ${position.from}`
      } ${panelClassName}`.trim()}
    >
      <ul
        ref={listRef}
        role="listbox"
        aria-label={label}
        aria-activedescendant={
          isOpen && options[focusedIndex]
            ? `${baseId}-${options[focusedIndex].value}`
            : undefined
        }
        className="max-h-64 overflow-y-auto rounded-xl border border-stroke-default bg-contrast-600 py-1 shadow-lift-popover"
      >
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isFocused = index === focusedIndex;
          return (
            <li
              key={option.value}
              id={`${baseId}-${option.value}`}
              role="option"
              aria-selected={isSelected}
              onClick={() => select(option.value)}
              className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-label-md transition-colors duration-fast ${
                isSelected
                  ? 'bg-primary-300/10 text-primary-300'
                  : isFocused
                    ? 'bg-lift-3 text-neutral-50'
                    : 'text-neutral-300 hover:bg-lift-3 hover:text-neutral-50'
              }`}
            >
              <span className="truncate font-medium">{option.label}</span>
              {isSelected && (
                <CheckIcon className="h-4 w-4 shrink-0 text-primary-300" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  const trigger = renderTrigger ? (
    renderTrigger(triggerState)
  ) : (
    <DefaultTrigger label={label} placement={placement} state={triggerState} />
  );

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`.trim()}
      data-testid={testId}
    >
      {/* The panel is absolutely positioned either way, so DOM order is free.
          Putting it first when it opens upward keeps reading order matching
          visual order. */}
      {placement === 'top' ? (
        <>
          {panel}
          {trigger}
        </>
      ) : (
        <>
          {trigger}
          {panel}
        </>
      )}
    </div>
  );
}

export default Listbox;
